import { useState, useEffect } from "react";
import { supabase, Profile, Product } from "../lib/supabase";

export interface ProviderWithProducts extends Profile {
  products: Product[];
}

export async function fetchProviders(): Promise<ProviderWithProducts[]> {
  const { data: providers } = await supabase
    .from("profiles")
    .select("*")
    .eq("role", "provider")
    .order("follower_count", { ascending: false });

  if (!providers || providers.length === 0) return [];

  const providerIds = providers.map((p) => p.id);
  const { data: products } = await supabase
    .from("products")
    .select("*")
    .in("provider_id", providerIds)
    .eq("visibility", "public")
    .order("sales_count", { ascending: false });

  return providers.map((p) => ({
    ...p,
    products: (products || []).filter((prod) => prod.provider_id === p.id),
  }));
}

export async function fetchProviderById(id: string): Promise<ProviderWithProducts | null> {
  const { data: provider } = await supabase
    .from("profiles")
    .select("*")
    .eq("id", id)
    .maybeSingle();

  if (!provider) return null;

  const { data: products } = await supabase
    .from("products")
    .select("*")
    .eq("provider_id", id)
    .eq("visibility", "public")
    .order("created_at", { ascending: false });

  // Get follower count
  const { count: followerCount } = await supabase
    .from("follows")
    .select("id", { count: "exact", head: true })
    .eq("provider_id", id);

  return {
    ...provider,
    follower_count: followerCount ?? provider.follower_count ?? 0,
    products: products || [],
  };
}

export function useProviders() {
  const [providers, setProviders] = useState<ProviderWithProducts[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProviders().then((data) => {
      setProviders(data);
      setLoading(false);
    });
  }, []);

  return { providers, loading };
}
